/**
 * InlineCharacterForm Component
 *
 * Simplified character creation form that appears inline within the wizard.
 * Allows users to create a new character without leaving the personalization flow.
 */

import { useCallback, useState } from 'react'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import {
  getAccountCharactersListQueryKey, useAccountCharactersCreate, useAccountCharactersUploadReferencePhotoCreate,
} from '@/shared/api/generated/characters/characters'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft'
import { faCamera } from '@fortawesome/pro-light-svg-icons/faCamera'
import { faSpinner } from '@fortawesome/pro-light-svg-icons/faSpinner'
import { faUpload } from '@fortawesome/pro-light-svg-icons/faUpload'
import { Alert, Box, Chip, styled, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

interface InlineCharacterFormProps {
  onBack: () => void
  onSuccess: (characterId: number) => void
}

type Gender = 'boy' | 'girl'

export const InlineCharacterForm: React.FC<InlineCharacterFormProps> = ({
  onBack,
  onSuccess,
}) => {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [age, setAge] = useState('')
  const [gender, setGender] = useState<Gender | null>(null)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { mutateAsync: createCharacter, isPending: isCreating } = useAccountCharactersCreate()
  const { mutateAsync: uploadPhoto, isPending: isUploading } = useAccountCharactersUploadReferencePhotoCreate()

  const isSubmitting = isCreating || isUploading

  const handlePhotoChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file')

        return
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image must be less than 10MB')

        return
      }

      setPhotoFile(file)
      setError(null)

      // Create preview
      const reader = new FileReader()

      reader.onloadend = () => {
        setPhotoPreview(reader.result as string)
      }

      reader.readAsDataURL(file)
    }
  }, [])

  const handleSubmit = async () => {
    // Validate
    if (!name.trim()) {
      setError('Please enter a name')

      return
    }

    if (!photoFile) {
      setError('Please upload a photo')

      return
    }

    setError(null)

    try {
      // Create character first
      const character = await createCharacter({
        data: {
          name: name.trim(),
          character_type: 'child',
          species: 'human',
        },
      })

      // Then upload photo
      await uploadPhoto({
        id: character.id,
        data: {
          file: photoFile,
        },
      })

      // Invalidate characters list cache
      queryClient.invalidateQueries({ queryKey: getAccountCharactersListQueryKey() })

      // Notify parent
      onSuccess(character.id)
    } catch (err) {
      console.error('Failed to create character:', err)
      setError('Failed to create character. Please try again.')
    }
  }

  const isValid = name.trim() && photoFile

  return (
    <StyledFormContainer>
      {/* Back button */}
      <Button text onClick={onBack} sx={{ mb: 2, ml: -1 }}>
        <Icon icon={faArrowLeft} style={{ marginRight: 8 }} />
        Back to character selection
      </Button>

      <Typography variant="h5" gutterBottom>
        Add a New Character
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a clear photo of your child and we&apos;ll create a personalized avatar for their story.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Photo Upload */}
      <StyledUploadSection>
        <input
          type="file"
          accept="image/*"
          onChange={handlePhotoChange}
          id="character-photo-upload"
          style={{ display: 'none' }}
        />
        <label htmlFor="character-photo-upload">
          <StyledUploadBox $hasPhoto={!!photoPreview}>
            {photoPreview ? (
              <StyledPhotoPreview src={photoPreview} alt="Preview" />
            ) : (
              <>
                <StyledUploadIcon>
                  <Icon icon={faCamera} sx={{ fontSize: pxToRem(32) }} />
                </StyledUploadIcon>
                <Typography variant="body2" fontWeight="medium">
                  Click to upload photo
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Clear face photo works best
                </Typography>
              </>
            )}
            {photoPreview && (
              <StyledChangePhotoOverlay>
                <Icon icon={faUpload} sx={{ mr: 1 }} />
                Change Photo
              </StyledChangePhotoOverlay>
            )}
          </StyledUploadBox>
        </label>
      </StyledUploadSection>

      {/* Name Input */}
      <TextField
        label="Child's Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        placeholder="Enter your child's name"
        sx={{ mb: 2 }}
      />

      {/* Age Input */}
      <TextField
        label="Age (optional)"
        value={age}
        onChange={(e) => setAge(e.target.value.replace(/\D/g, ''))}
        fullWidth
        placeholder="How old is your child?"
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        sx={{ mb: 2 }}
      />

      {/* Gender Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
          Gender (optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label="Boy"
            onClick={() => setGender(gender === 'boy' ? null : 'boy')}
            variant={gender === 'boy' ? 'filled' : 'outlined'}
            color={gender === 'boy' ? 'primary' : 'default'}
            sx={{ px: 2 }}
          />
          <Chip
            label="Girl"
            onClick={() => setGender(gender === 'girl' ? null : 'girl')}
            variant={gender === 'girl' ? 'filled' : 'outlined'}
            color={gender === 'girl' ? 'primary' : 'default'}
            sx={{ px: 2 }}
          />
        </Box>
      </Box>

      {/* Submit Button */}
      <Button
        primary
        fullWidth
        size="large"
        onClick={handleSubmit}
        disabled={!isValid || isSubmitting}
        sx={{ py: 1.5 }}
      >
        {isSubmitting ? (
          <>
            <Icon icon={faSpinner} spin style={{ marginRight: 8 }} />
            Creating Character...
          </>
        ) : (
          'Create Character'
        )}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        The AI will generate a personalized avatar based on the photo.
        This may take a few minutes.
      </Typography>
    </StyledFormContainer>
  )
}

const StyledFormContainer = styled(Box)`
  max-width: ${pxToRem(500)};
  margin: 0 auto;
  padding: ${pxToRem(24)};
  background-color: ${props => props.theme.palette.background.paper};
  border-radius: ${pxToRem(16)};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`

const StyledUploadSection = styled(Box)`
  margin-bottom: ${pxToRem(24)};
`

const StyledUploadBox = styled(Box, {
  shouldForwardProp: prop => prop !== '$hasPhoto',
})<{ $hasPhoto: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${pxToRem(200)};
  aspect-ratio: 1;
  margin: 0 auto;
  border: 2px dashed ${props =>
    props.$hasPhoto
      ? props.theme.palette.primary.main
      : props.theme.palette.divider};
  border-radius: ${pxToRem(16)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  overflow: hidden;

  &:hover {
    border-color: ${props => props.theme.palette.primary.main};
    background-color: ${props =>
    props.$hasPhoto ? 'transparent' : props.theme.palette.action.hover};
  }
`

const StyledUploadIcon = styled(Box)`
  width: ${pxToRem(64)};
  height: ${pxToRem(64)};
  border-radius: 50%;
  background-color: ${props => props.theme.palette.custom.lightpurple};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${pxToRem(12)};
  color: ${props => props.theme.palette.primary.main};
`

const StyledPhotoPreview = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StyledChangePhotoOverlay = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${pxToRem(12)};
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${pxToRem(14)};
  font-weight: 500;
`

export default InlineCharacterForm
